import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Mail, Loader2, Send } from "lucide-react";
import { API, api } from "../lib/api";

const LOGO_COLOR =
  "/assets/logo-fped-color.svg";

const ERROR_MESSAGES = {
  oauth_failed: "Autentificarea Google a eșuat. Încearcă din nou.",
  email_not_verified: "Contul Google nu este verificat.",
  wrong_workspace: "Trebuie să folosești un cont Google Workspace @fped.org.",
  domain_not_allowed: "Domeniul tău nu are acces prin Google. Cere un link de acces ca operator extern.",
  not_authorized: "Acest email nu are acces la dashboard. Contactează un owner.",
  account_inactive: "Contul tău este dezactivat. Contactează un owner.",
};

export default function AdminLogin() {
  const [params] = useSearchParams();
  const [errorMsg, setErrorMsg] = useState(null);
  const [mode, setMode] = useState("google"); // "google" | "magic"
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    const code = params.get("error");
    if (code) setErrorMsg(ERROR_MESSAGES[code] || "Autentificare eșuată.");
  }, [params]);

  // REMINDER: DO NOT HARDCODE THE URL OR ADD ANY FALLBACKS / REDIRECT URLS — THIS BREAKS THE AUTH.
  // The backend resolves OAUTH_REDIRECT_URI itself; the frontend just navigates the browser there.
  const startLogin = () => {
    window.location.href = `${API}/auth/google/start`;
  };

  const requestMagicLink = async (e) => {
    e.preventDefault();
    if (!magicEmail.trim()) return;
    setMagicSending(true);
    try {
      // Anti-enumeration: backend always returns 200. We always show the same "sent" UI.
      await api.post("/auth/magic/request", { email: magicEmail.trim().toLowerCase() });
      setMagicSent(true);
    } catch {
      // Even on transient errors we surface the "sent" state to avoid leaking signals.
      setMagicSent(true);
    } finally {
      setMagicSending(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F5F6F6] grid place-items-center px-4 sm:px-6 py-10"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-[#E5E7EB] px-7 sm:px-12 py-10 sm:py-14 shadow-xl flex flex-col items-center text-center">
          <img
            src={LOGO_COLOR}
            alt="Fundația pentru Educație Digitală"
            className="h-12 sm:h-14 w-auto"
            data-testid="admin-login-logo"
          />

          <div className="mt-8 h-12 w-12 rounded-2xl bg-[#373A45] grid place-items-center text-white">
            <Lock size={20} />
          </div>

          <h1 className="mt-6 font-display text-2xl font-bold text-[#373A45]">
            Admin Dashboard
          </h1>

          {errorMsg && (
            <div
              className="mt-6 w-full rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 p-3 text-sm text-[#9b1f2c]"
              data-testid="admin-login-error"
              role="alert"
            >
              {errorMsg}
            </div>
          )}

          {mode === "google" && (
            <>
              <p className="mt-2 text-sm text-[#6B7280] max-w-xs">
                Autentificare prin contul tău Google Workspace <strong>@fped.org</strong>.
              </p>
              <button
                onClick={startLogin}
                className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-white border-2 border-[#E5E7EB] hover:border-[#965EA7] hover:shadow-[0_4px_14px_rgba(150,94,167,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7]/50 text-[#373A45] font-semibold py-3 transition-all"
                data-testid="admin-google-login"
              >
                <GoogleIcon />
                Sign in with Google
              </button>

              <div className="mt-8 pt-6 w-full border-t border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold">
                  Parteneri
                </p>
                <button
                  onClick={() => { setMode("magic"); setMagicSent(false); setErrorMsg(null); }}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5F0F8] hover:bg-[#EDDFF2] text-[#7A4986] font-semibold py-2.5 text-sm transition-colors"
                  data-testid="admin-login-magic-toggle"
                >
                  <Mail size={14} />
                  Cere un link de acces
                </button>
              </div>
            </>
          )}

          {mode === "magic" && !magicSent && (
            <form onSubmit={requestMagicLink} className="mt-6 w-full text-left" data-testid="admin-magic-request-form">
              <p className="text-sm text-[#6B7280] mb-4">
                Introdu emailul tău. Dacă ai acces, vei primi un link unic, valabil <strong>15 minute</strong>.
              </p>
              <label className="block">
                <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">Email</span>
                <input
                  required
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="ex. operator@partener.ro"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  className="mt-1.5 w-full bg-white border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none text-sm"
                  data-testid="admin-magic-email-input"
                />
              </label>
              <button
                type="submit"
                disabled={magicSending}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60 text-white font-semibold py-3 transition-all"
                data-testid="admin-magic-submit"
              >
                {magicSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={14} />}
                {magicSending ? "Se trimite..." : "Trimite link"}
              </button>
              <button
                type="button"
                onClick={() => setMode("google")}
                className="mt-3 w-full text-xs text-[#6B7280] hover:text-[#373A45] underline underline-offset-2"
              >
                Înapoi la Google login
              </button>
            </form>
          )}

          {mode === "magic" && magicSent && (
            <div className="mt-6 w-full text-left" data-testid="admin-magic-sent">
              <div className="rounded-xl border border-[#519872]/30 bg-[#519872]/5 p-4 text-sm text-[#2f6b50]">
                <strong className="block mb-1">Ți-am trimis un link de acces</strong>
                Dacă emailul există în lista noastră, vei primi un email cu instrucțiuni în câteva secunde.
                Linkul este valabil <strong>15 minute</strong> și poate fi folosit o singură dată.
              </div>
              <button
                type="button"
                onClick={() => { setMode("google"); setMagicSent(false); setMagicEmail(""); }}
                className="mt-5 w-full text-xs text-[#6B7280] hover:text-[#373A45] underline underline-offset-2"
              >
                Înapoi la pagina de login
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#965EA7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7]/50 rounded-full px-3 py-1.5 transition-colors"
            data-testid="admin-login-home-link"
          >
            <ArrowLeft size={14} />
            Înapoi la pagina principală
          </Link>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
